import {
  type ChangeEventHandler,
  forwardRef,
  type MouseEventHandler,
  type MutableRefObject,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { EVENT_KEY, MLPVersion } from '../types/enums';
import Tooltip from '../Tooltip/Tooltip';
import { useClickOutsideDetector } from '../hooks';
import RawTextLink from '../TextLink/RawTextLink';
import { TextLinkUnderlineStyle } from '../TextLink';
import { SkeletonFrame } from '../SkeletonFrame';
import styles from './Search.module.scss';

import type { Data, SearchPropsTypes, SearchTheme } from './types/propsType';

const cx = classNames.bind(styles);

const getDisabledClass = (disabled: boolean) => (disabled ? styles['disabled-container'] : '');

const getDrawerDisplayClass = (drawerDisplay: boolean, disabled: boolean) =>
  drawerDisplay && !disabled ? styles['open-search-result'] : '';
const getIsMLP2Variant = (isMLP2Variant: boolean, onClick: React.MouseEventHandler<HTMLSpanElement> | undefined) =>
  isMLP2Variant && (
    <FAIcon
      iconName={'magnifying-glass'}
      className={`${styles['icon']} ${styles['search-mlp2-left-icon']}`}
      data-testid="search-mlp2-icon-test-left-id"
      onClick={onClick}
    />
  );

const getMagnifyingGlass = (
  isMLP2Variant: boolean,
  value: string | number,
  isTyping: boolean,
  onClick: React.MouseEventHandler<HTMLSpanElement> | undefined,
) =>
  !isMLP2Variant &&
  (!value || isTyping) && (
    <span>
      <FAIcon
        iconName={'magnifying-glass'}
        iconStyle={IconStyle.SOLID}
        className={`${styles['icon']} ${styles['icon-desktop']}`}
        data-testid="search-icon-test-right-id"
        onClick={onClick}
      />
    </span>
  );

const getCloseXMark = (
  isMLP2Variant: boolean,
  value: string | number,
  isTyping: boolean,
  onClick: React.MouseEventHandler<HTMLSpanElement> | undefined,
) =>
  value &&
  (!isTyping || isMLP2Variant) && (
    <span className={styles['close-icon']}>
      <FAIcon
        iconName={isMLP2Variant ? 'close' : 'xmark-large'}
        iconStyle={IconStyle.SOLID}
        className={`${styles['icon']} ${!isMLP2Variant ? styles['icon-medium'] : ''}`}
        data-testid="clear-icon-test-id"
        onClick={(e) => onClick?.(e)}
      />
    </span>
  );

/**
 * @description Provides Search Field
 */
const Search = forwardRef<HTMLSpanElement, SearchPropsTypes>(
  (
    {
      value,
      selectionActiveState = false,
      data = [],
      placeholder = '',
      disabled = false,
      onChange,
      onSearch,
      configData,
      onClear,
      onClick,
      className = '',
      mlpVersion = MLPVersion.ONE,
      label,
      labelInfo,
      theme,
      message,
      searchResultsCta,
      noMatchMessage = '',
      focusOnMount = false,
      autoComplete = 'off',
      loading,
      ...restProps
    }: SearchPropsTypes,
    ref,
  ) => {
    const searchRef = useRef() as MutableRefObject<HTMLSpanElement>;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const searchIconRef = useRef() as MutableRefObject<HTMLSpanElement>;
    const [drawerDisplay, setDrawerDisplay] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const isMLP2Variant = mlpVersion === MLPVersion.TWO;
    useImperativeHandle(ref, () => searchRef.current);
    useEffect(() => {
      setDrawerDisplay(data.length > 0 || noMatchMessage !== '');
    }, [data, noMatchMessage]);

    useEffect(() => {
      if (focusOnMount && !disabled) {
        inputRef.current?.focus();
      }
    }, [focusOnMount, disabled]);

    useClickOutsideDetector(searchRef, () => {
      setDrawerDisplay(false);
      setIsTyping(false);
    });
    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      setIsTyping(true);
      onChange(e);
    };
    const onClickHandler: MouseEventHandler<HTMLInputElement> = (e) => onClick?.(e);
    const CClassname = cx('search-container', [theme], className);
    const CMainClassname = cx('search-box', {
      'search-field-result': drawerDisplay && !disabled,
      'search-mlp2': isMLP2Variant,
    });
    const CResultsCtaClassname = cx('cta-container', {
      loading: loading,
    });
    return (
      <section className={CClassname} id="nextgen-search-component">
        {label && !isMLP2Variant && (
          <section className={styles['search-field-label-container']}>
            <label htmlFor={restProps.id} className={styles['search-field-label']}>
              {label}
            </label>
            {restProps?.required && <sup>*</sup>}
            {labelInfo && <Tooltip {...optionalProp('preference', restProps.preference)} message={labelInfo} />}
          </section>
        )}
        <span className={`${styles['search-body-container']} ${getDisabledClass(disabled)}`} ref={searchRef}>
          {/* Search Box with Icon */}
          <section className={CMainClassname}>
            {getIsMLP2Variant(isMLP2Variant, onSearch)}
            <input
              ref={inputRef}
              value={value}
              onChange={handleChange}
              className={styles['search-field']}
              placeholder={placeholder}
              onKeyDown={(e) => {
                if (e.key === EVENT_KEY.ENTER) {
                  onSearch?.();
                }
              }}
              onFocus={() => {
                setDrawerDisplay(data.length > 0 || noMatchMessage !== '' || false);
              }}
              disabled={disabled}
              onClick={onClickHandler}
              data-testid="search-test-id"
              autoComplete={autoComplete}
              aria-label={placeholder}
              {...restProps}
            />
            <span ref={drawerDisplay ? null : searchIconRef}>
              {getMagnifyingGlass(isMLP2Variant, value, isTyping, onSearch)}
              {getCloseXMark(isMLP2Variant, value, isTyping, (e) => {
                onClear?.(e);
                setDrawerDisplay(false);
              })}
            </span>
          </section>
          {/* Search Result Drawer */}
          {!isMLP2Variant && (
            <section
              className={`${styles['base-search-result']} ${getDrawerDisplayClass(drawerDisplay, disabled)}`}
              role="tabpanel"
            >
              <ul role="tablist">
                {data.map((item: Data, index) => {
                  const optionLabel = item[configData?.label as string];
                  const title = ['string', 'number', 'boolean'].includes(typeof optionLabel)
                    ? String(optionLabel)
                    : undefined;
                  return (
                    <li
                      title={title}
                      role="tab"
                      tabIndex={0}
                      key={`search${+index}`}
                      className={`${styles[`selected-item-${item.selected}`]} ${
                        styles[`hover-item-active-state-${selectionActiveState}`]
                      }`}
                      onClick={() => {
                        configData?.onSelect(item);
                        setTimeout(() => {
                          setDrawerDisplay(false);
                        }, 0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === EVENT_KEY.ENTER) {
                          configData?.onSelect(item);
                          setTimeout(() => {
                            setDrawerDisplay(false);
                          }, 0);
                        }
                      }}
                      data-testid="search-result-testid"
                    >
                      <p>{optionLabel as ReactNode}</p>
                    </li>
                  );
                })}
              </ul>
              {data.length === 0 && noMatchMessage !== '' && (
                <p className={styles['not-found-message']} data-testid={'not-found-message'}>
                  {noMatchMessage}
                </p>
              )}
              {searchResultsCta?.text && (
                <div className={CResultsCtaClassname}>
                  {loading ? (
                    <SkeletonFrame heightValue={'16'} widthValue={'120'} borderRadiusValue={'6'} isRoundCorner={true} />
                  ) : (
                    <RawTextLink
                      underline={TextLinkUnderlineStyle.NOHOVER}
                      {...searchResultsCta}
                      data-testid={'search-results-cta'}
                      className={styles['results-cta']}
                    />
                  )}
                </div>
              )}
            </section>
          )}
          {message && <span className={styles['search-message']}>{message}</span>}
        </span>
      </section>
    );
  },
);
Search.displayName = 'Search';
export type { SearchTheme, Data };
export default Search;
